import { Check } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export interface PickerOption {
  value: string;
  label: string;
  icon?: ReactNode;
  hint?: string;
  /** Secondary line rendered under the label. */
  description?: string;
}

/**
 * Linear-style picker: a popover with a command palette. Single select closes
 * on pick; multi select stays open and toggles.
 */
export function Picker({
  options,
  selected,
  onToggle,
  multiple = true,
  placeholder = "Filter…",
  children,
  align = "start",
  open: controlledOpen,
  onOpenChange,
  className,
  readOnly = false,
}: {
  options: PickerOption[];
  selected: string[];
  onToggle: (value: string) => void;
  multiple?: boolean;
  placeholder?: string;
  children: ReactNode;
  align?: "start" | "center" | "end";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  /** Shows the trigger as a plain value that does not open. */
  readOnly?: boolean;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = onOpenChange ?? setUncontrolledOpen;
  if (readOnly) return <div className="pointer-events-none">{children}</div>;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        className={cn("w-64 p-0", className)}
        align={align}
        // Focusing the filter on a touch screen raises the keyboard over the options.
        onOpenAutoFocus={(event) => window.matchMedia("(pointer: coarse)").matches && event.preventDefault()}
      >
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>Nothing matches.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const active = selected.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={`${option.label} ${option.description ?? ""} ${option.value}`}
                    onSelect={() => {
                      onToggle(option.value);
                      if (!multiple) setOpen(false);
                    }}
                  >
                    {option.icon}
                    {option.description ? (
                      <div className="min-w-0 flex-1">
                        <p className="truncate">{option.label}</p>
                        <p className="truncate text-xs text-muted-foreground">{option.description}</p>
                      </div>
                    ) : (
                      <span className="truncate">{option.label}</span>
                    )}
                    {option.hint && (
                      <span className="ml-auto text-xs text-muted-foreground">{option.hint}</span>
                    )}
                    <Check className={cn("ml-auto size-4", active ? "opacity-100" : "opacity-0")} />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
